import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Book, Menu, Sunset, Trees, Zap } from "lucide-react";
import { cn } from "../lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, } from "./ui/accordion";
import { Button } from "./ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, } from "./ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, } from "./ui/sheet";

const Navbar1 = ({ 
  logo = { 
    url: "https://www.shadcnblocks.com", 
    src: "https://www.shadcnblocks.com/images/block/block-1.svg", 
    alt: "logo", 
    title: "Shadcnblocks.com", 
  }, 
  menu = [
    { title: "Home", url: "#" },
    { 
      title: "Products", 
      url: "#", 
      items: [
        { title: "Blog", description: "The latest industry news, updates, and info", icon: <Book className="size-5 shrink-0" />, url: "#", },
        { title: "Company", description: "Our mission is to innovate and empower the world", icon: <Trees className="size-5 shrink-0" />, url: "#", },
        { title: "Careers", description: "Browse job listing and discover our workspace", icon: <Sunset className="size-5 shrink-0" />, url: "#", },
      ], 
    },
    { 
      title: "Resources", 
      url: "#", 
      items: [
        { title: "Help Center", description: "Get all the answers you need right here", icon: <Zap className="size-5 shrink-0" />, url: "#", },
        { title: "Contact Us", description: "We are here to help you with any questions you have", icon: <Sunset className="size-5 shrink-0" />, url: "#", },
        { title: "Status", description: "Check the current status of our services and APIs", icon: <Trees className="size-5 shrink-0" />, url: "#", },
      ], 
    },
    { title: "Pricing", url: "#", },
    { title: "Blog", url: "#", },
  ], 
  mobileExtraLinks = [
    { name: "Press", url: "#" },
    { name: "Contact", url: "#" },
    { name: "Imprint", url: "#" },
    { name: "Sitemap", url: "#" },
  ], 
  auth = {
    login: { text: "Log in", url: "#" },
    signup: { text: "Sign up", url: "#" },
  },
  // Clases extra para la pastilla. Existe por /landing-2, que tiene el hero
  // sobre fondo oscuro: ahí el `bg-white/80` de abajo es blanco al 80% sobre
  // negro, o sea gris sucio. Es opcional y aditiva, así que Home, Plans y la
  // demo siguen exactamente igual sin tocarlas.
  claseDeBarra,
  /* Cómo se posiciona el <header>. Por defecto `sticky top-0`, que es lo que
     venía haciendo y lo que usa Home. Existe porque en /landing-2 la barra
     tiene un ancestro que la encierra —la caja de la primera pantalla, que
     mide una pantalla— y `sticky` se pega contra su ancestro de bloque, así
     que ahí dejaba de acompañar el scroll apenas terminaba el hero. */
  clasePosicion = 'sticky top-0',
}) => {
  // La barra acompaña todo el scroll y se compacta al bajar, para ocupar menos
  // pantalla sin desaparecer. Antes era `relative`, así que se iba con el scroll
  // y para volver al menú había que subir hasta arriba.
  const [compacta, setCompacta] = useState(false);

  useEffect(() => {
    const alScrollear = () => setCompacta(window.scrollY > 24);

    alScrollear(); // por si la página abre ya scrolleada (recarga o link con ancla)
    // passive: no llamamos preventDefault, y así el navegador no espera al handler
    // para scrollear.
    window.addEventListener("scroll", alScrollear, { passive: true });
    return () => window.removeEventListener("scroll", alScrollear);
  }, []);

  return (
    <header className={cn(clasePosicion, 'z-50 px-3 pt-3 sm:px-4 sm:pt-4')}>
      {/* La pastilla. El fondo es translúcido con blur para que el degradado de
          la landing siga viéndose por detrás. */}
      <div
        className={cn(
          "mx-auto max-w-6xl rounded-full border border-gray-200/70 bg-white/80 backdrop-blur-md transition-all duration-300",
          compacta ? "px-4 py-1.5 shadow-lg sm:px-5" : "px-5 py-3 shadow-sm sm:px-6",
          claseDeBarra
        )}
      >
        {/* `items-center`: sin él los hijos se estiran a lo alto de la fila, que
            la marca el logo (48px). Los botones tienen alto fijo (36px), así que
            quedaban apoyados arriba de ese bloque estirado —13px de aire arriba
            y 25 abajo— en vez de centrados en la barra. */}
        <nav className="hidden items-center justify-between lg:flex">
          <div className="flex items-center gap-6">
            {logo.onClick ? (
              <button onClick={logo.onClick} className="flex items-center gap-2">
                {logo.src && (
                  <img
                    src={logo.src}
                    className={cn("transition-all duration-300", compacta ? "w-9" : "w-12")}
                    alt={logo.alt}
                  />
                )}
              </button>
            ) : (
              <Link to={logo.url} className="flex items-center gap-2">
                {logo.src && (
                  <img
                    src={logo.src}
                    className={cn("transition-all duration-300", compacta ? "w-9" : "w-12")}
                    alt={logo.alt}
                  />
                )}
              </Link>
            )}
            <div className="flex items-center">
              <NavigationMenu>
                <NavigationMenuList>
                  {menu.map((item) => renderMenuItem(item))}
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              asChild 
              variant="outline" 
              size="sm"
              className="bg-white/95 backdrop-blur-sm border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg font-medium"
            >
              <Link to={auth.login.url}>{auth.login.text}</Link>
            </Button>
            <Button 
              asChild 
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-medium border-0"
            >
              <Link to={auth.signup.url}>{auth.signup.text}</Link>
            </Button>
          </div>
        </nav>
        <div className="block lg:hidden">
          <div className="flex items-center justify-between">
            {logo.onClick ? (
              <button onClick={logo.onClick} className="flex items-center gap-2">
                {logo.src && <img src={logo.src} className="w-12" alt={logo.alt} />}
              </button>
            ) : (
              <Link to={logo.url} className="flex items-center gap-2">
                {logo.src && <img src={logo.src} className="w-12" alt={logo.alt} />}
              </Link>
            )}
            <Sheet>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="bg-white/95 backdrop-blur-sm border-gray-300 text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg"
                >
                  <Menu className="size-4" />
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto bg-white/95 backdrop-blur-md">
                <SheetHeader>
                  <SheetTitle>
                    <Link to={logo.url} className="flex items-center gap-2">
                      {logo.src && <img src={logo.src} className="w-12" alt={logo.alt} />}
                    </Link>
                  </SheetTitle>
                </SheetHeader>
                <div className="my-6 flex flex-col gap-6">
                  <Accordion type="single" collapsible className="flex w-full flex-col gap-4" >
                    {menu.map((item) => renderMobileMenuItem(item))}
                  </Accordion>
                  <div className="border-t py-4">
                    <div className="grid grid-cols-2 justify-start">
                      {mobileExtraLinks.map((link, idx) => (
                        <Link
                          key={idx}
                          className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                          to={link.url}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <Button 
                      asChild 
                      variant="outline"
                      className="bg-white/95 backdrop-blur-sm border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg font-medium"
                    >
                      <Link to={auth.login.url}>{auth.login.text}</Link>
                    </Button>
                    <Button 
                      asChild
                      className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-medium border-0"
                    >
                      <Link to={auth.signup.url}>{auth.signup.text}</Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

const renderMenuItem = (item) => {
  if (item.items) {
    return (
      <NavigationMenuItem key={item.title} className="relative text-gray-700 hover:text-gray-900">
        <NavigationMenuTrigger className="bg-transparent hover:bg-gray-100/80 rounded-md px-3 py-2 font-medium">
          {item.title}
        </NavigationMenuTrigger>
        <NavigationMenuContent className="absolute left-0 bg-white/95 backdrop-blur-md border border-gray-200 shadow-xl rounded-lg z-50">
          <ul className="w-80 p-3">
            <NavigationMenuLink>
              {item.items.map((subItem) => (
                <li key={subItem.title}>
                  <button
                    className="flex select-none gap-4 rounded-md p-3 leading-none no-underline outline-none hover:bg-gray-100 hover:text-gray-900 w-full text-left"
                    onClick={subItem.onClick || (() => {})}
                  >
                    {subItem.icon}
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {subItem.title}
                      </div>
                      {subItem.description && (
                        <p className="text-sm leading-snug text-gray-600">
                          {subItem.description}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </NavigationMenuLink>
          </ul>
        </NavigationMenuContent>
      </NavigationMenuItem>
    );
  }
  
  // Si tiene onClick, usar button, si no, usar Link
  if (item.onClick) {
    return (
      <button
        key={item.title}
        className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
        onClick={item.onClick}
      >
        {item.title}
      </button>
    );
  }
  
  return (
    <Link
      key={item.title}
      className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
      to={item.url}
    >
      {item.title}
    </Link>
  );
};

const renderMobileMenuItem = (item) => {
  if (item.items) {
    return (
      <AccordionItem key={item.title} value={item.title} className="border-b-0">
        <AccordionTrigger className="py-0 font-semibold text-gray-900 hover:no-underline hover:text-gray-700">
          {item.title}
        </AccordionTrigger>
        <AccordionContent className="mt-2">
          {item.items.map((subItem) => (
            <button
              key={subItem.title}
              className="flex select-none gap-4 rounded-md p-3 leading-none outline-none hover:bg-gray-100 hover:text-gray-900 w-full text-left"
              onClick={subItem.onClick || (() => {})}
            >
              {subItem.icon}
              <div>
                <div className="text-sm font-semibold text-gray-900">{subItem.title}</div>
                {subItem.description && (
                  <p className="text-sm leading-snug text-gray-600">
                    {subItem.description}
                  </p>
                )}
              </div>
            </button>
          ))}
        </AccordionContent>
      </AccordionItem>
    );
  }
  
  // Si tiene onClick, usar button, si no, usar Link
  if (item.onClick) {
    return (
      <button
        key={item.title}
        className="font-semibold text-gray-900 hover:text-gray-700 w-full text-left py-2"
        onClick={item.onClick}
      >
        {item.title}
      </button>
    );
  }
  
  return (
    <Link key={item.title} to={item.url} className="font-semibold text-gray-900 hover:text-gray-700">
      {item.title}
    </Link>
  );
};

export { Navbar1 };
